import { useCallback, useEffect, useMemo, useState } from 'react'

import { useNavigate } from 'react-router'
import { Link } from 'react-router-dom'

import IconChevronLeft from '@/assets/icons/icon-chevron-left.svg'
import IconEdit from '@/assets/icons/icon-edit.svg'
import IconMore from '@/assets/icons/icon-menu-more.svg'
import IconPlus from '@/assets/icons/icon-plus.svg'
import SearchIcon from '@/assets/icons/icon-search.svg'
import IconDelete from '@/assets/icons/icon-trash.svg'
import { Page } from '@/components/layout/page/page.tsx'
import { Error } from '@/components/ui/Error'
import { Button } from '@/components/ui/button.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import { CreateListDialog } from '@/modules/tasks/components/create-list-dialog'
import { TaskEditorDialog } from '@/modules/tasks/components/create-task-dialog/task-editor-dialog'
import { DeleteListDialog } from '@/modules/tasks/components/delete-list-dialog'
import { DeleteTaskDialog } from '@/modules/tasks/components/delete-task-dialog'
import { useIcon } from '@/modules/tasks/components/icons'
import { TaskListPageSkeleton } from '@/modules/tasks/components/task-list-page-skeleton'
import type { NavigationEntry } from '@/modules/tasks/components/task-navigation'
import { TaskTable } from '@/modules/tasks/components/task-table'
import { useList } from '@/modules/tasks/query/task-list-query'
import { useUpdateTask } from '@/modules/tasks/query/task-query'
import type { ListForm } from '@/modules/tasks/service/task-list-service'
import { AppRoutes } from '@/utils/constants'

export interface TaskListPageProps {
  id: number
}

export const TaskListPage = ({ id }: TaskListPageProps) => {
  const list = useList(id)
  const [tasks, setTasks] = useState<NavigationEntry[]>([])

  const updateTask = useUpdateTask()

  useEffect(
    () =>
      setTasks(list.data?.tasks?.map((it) => ({ ...it, type: 'task' })) ?? []),
    [list.data?.tasks],
  )

  const handleToggleCompleted = useCallback(
    (task: NavigationEntry, state: boolean) => {
      updateTask
        .mutateAsync({ id: task.id, completed: state })
        .then(() => list.refetch())
    },
    [list, updateTask],
  )

  const initialEditForm = useMemo<ListForm | undefined>(
    () => ({
      id: list.data?.id,
      tasks: list.data?.tasks?.map((task) => task.id) ?? [],
      name: list.data?.name ?? '',
      icon: list.data?.icon ?? '',
      color: list.data?.color ?? '',
      detail: list.data?.detail ?? '',
    }),
    [list],
  )

  const [showEditList, setShowEditList] = useState(false)

  const handleShowEditList = useCallback(() => setShowEditList(true), [])

  const handleCloseEditList = useCallback(() => setShowEditList(false), [])

  const [showDeleteList, setShowDeleteList] = useState(false)

  const handleShowDeleteList = useCallback(() => setShowDeleteList(true), [])

  const handleCloseDeleteList = useCallback(() => setShowDeleteList(false), [])

  const navigate = useNavigate()

  const handleDeleted = useCallback(() => {
    navigate(AppRoutes.Tasks.List.asUrl())
  }, [navigate])

  const [showCreateTask, setShowCreateTask] = useState(false)
  const [edited, setEdited] = useState<NavigationEntry>()

  const handleShowEdit = useCallback((task: NavigationEntry) => {
    setEdited(task)
    setShowCreateTask(true)
  }, [])

  const handleShowAddTask = useCallback(() => {
    setEdited(undefined)
    setShowCreateTask(true)
  }, [])

  const [taskToDelete, setTaskToDelete] = useState<
    NavigationEntry | undefined
  >()
  const [showDelete, setShowDelete] = useState(false)

  const handleShowDelete = useCallback((task: NavigationEntry) => {
    setTaskToDelete(task)
    setShowDelete(true)
  }, [])

  const icon = useIcon(list.data?.icon, list.data?.color)

  if (list.isFetching) {
    return <TaskListPageSkeleton />
  }

  if (list.isError) {
    return (
      <Page>
        <Link to={AppRoutes.Tasks.List.asUrl()}>
          <span className="flex items-center gap-2 text-sm font-medium text-primary-500 hover:underline">
            <IconChevronLeft />
            Back to Tasks
          </span>
        </Link>
        <Error
          error={list.error}
          className="mt-4"
        />
      </Page>
    )
  }

  return (
    <Page>
      <Link to={AppRoutes.Tasks.List.asUrl()}>
        <span className="flex items-center gap-2 text-sm font-medium text-primary-500 hover:underline">
          <IconChevronLeft />
          Back to Tasks
        </span>
      </Link>
      <div>
        <div className="mb-3 mt-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <h3 className="flex items-center gap-2 text-[20px] font-semibold text-title">
            {icon}
            {list.data?.name}
            {list.data?.tasks && ` (${list.data?.tasks.length})`}
          </h3>
          <div className="flex items-center gap-3">
            <TextField
              prefix={
                <SearchIcon className="h-4 w-4 flex-shrink-0 text-title peer-focus:text-primary-500" />
              }
              placeholder="Search"
              className="w-56"
              type="text"
            />
            <Button onClick={handleShowAddTask}>
              <IconPlus />
              New Task
            </Button>
            <Popover>
              <PopoverTrigger>
                <Button
                  variant="outline"
                  className="px-3"
                >
                  <IconMore className="h-4 w-4 flex-shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[150px]">
                <div className="flex flex-col gap-2">
                  <div
                    className="flex cursor-pointer items-center gap-2 text-title transition duration-300 ease-out hover:text-primary-500"
                    onClick={handleShowEditList}
                  >
                    <IconEdit className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">Edit List</span>
                  </div>
                  <div
                    className="flex cursor-pointer items-center gap-2 text-title transition duration-300 ease-out hover:text-danger-default"
                    onClick={handleShowDeleteList}
                  >
                    <IconDelete className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">Delete List</span>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <TaskTable
          tasks={tasks}
          onToggleCompleted={handleToggleCompleted}
          onOpenEdit={handleShowEdit}
          onOpenDelete={handleShowDelete}
        />
        <TaskEditorDialog
          isOpen={showCreateTask}
          onVisibilityChange={setShowCreateTask}
          taskList={list.data}
          task={edited}
        />
        <DeleteTaskDialog
          task={taskToDelete}
          show={showDelete}
          onVisibilityChange={setShowDelete}
        />
        <DeleteListDialog
          list={list.data}
          show={showDeleteList}
          onDeleted={handleDeleted}
          onVisibilityChange={handleCloseDeleteList}
        />
        <CreateListDialog
          initial={initialEditForm}
          isOpen={showEditList}
          onClose={handleCloseEditList}
        />
      </div>
    </Page>
  )
}
